import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { industries, countries, employmentTypes } from "../data/formOptions";
import {
  Bell,
  BellPlus,
  BellOff,
  Plus,
  X,
  Trash2,
  Edit2,
  Check,
  Loader2,
  MapPin,
  Briefcase,
  Building2,
  DollarSign,
  Clock,
  Search,
  ChevronDown,
  Play,
  Pause,
  AlertCircle
} from "lucide-react";

const FREQUENCY_OPTIONS = [
  { value: "instant", label: "Instant", description: "Get notified immediately" },
  { value: "daily", label: "Daily Digest", description: "Once per day" },
  { value: "weekly", label: "Weekly Digest", description: "Once per week" }
];

const SENIORITY_OPTIONS = [
  { value: "entry", label: "Entry Level" },
  { value: "mid", label: "Mid Level" },
  { value: "senior", label: "Senior Level" },
  { value: "lead", label: "Lead / Manager" },
  { value: "executive", label: "Executive" }
];

const JobAlerts = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResults, setTestResults] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    industries: [],
    employment_types: [],
    locations: [],
    keywords: [],
    min_salary: "",
    seniority_levels: [],
    frequency: "daily"
  });
  
  const [keywordInput, setKeywordInput] = useState("");
  const [locationInput, setLocationInput] = useState("");

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/job-alerts`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error("Failed to fetch alerts:", error);
      toast.error("Failed to load job alerts");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAlert = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter an alert name");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        min_salary: formData.min_salary ? parseInt(formData.min_salary) : null
      };
      
      const response = await fetch(`${API}/job-alerts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(prev => [data.alert, ...prev]);
        setShowCreateForm(false);
        resetForm();
        toast.success("Job alert created!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create alert");
      }
    } catch (error) {
      toast.error("Failed to create alert");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAlert = async () => {
    if (!editingAlert) return;
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        min_salary: formData.min_salary ? parseInt(formData.min_salary) : null
      };
      
      const response = await fetch(`${API}/job-alerts/${editingAlert.alert_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setAlerts(prev => prev.map(a => 
          a.alert_id === editingAlert.alert_id ? data.alert : a
        ));
        setEditingAlert(null);
        resetForm();
        toast.success("Job alert updated!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update alert");
      }
    } catch (error) {
      toast.error("Failed to update alert");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    setDeletingId(alertId);
    try {
      const response = await fetch(`${API}/job-alerts/${alertId}`, {
        method: "DELETE",
        credentials: "include",
      });
      
      if (response.ok) {
        setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
        toast.success("Job alert deleted");
      } else {
        toast.error("Failed to delete alert");
      }
    } catch (error) {
      toast.error("Failed to delete alert");
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleAlert = async (alert) => {
    try {
      const response = await fetch(`${API}/job-alerts/${alert.alert_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !alert.is_active })
      });
      
      if (response.ok) {
        setAlerts(prev => prev.map(a => 
          a.alert_id === alert.alert_id ? { ...a, is_active: !a.is_active } : a
        ));
        toast.success(alert.is_active ? "Alert paused" : "Alert activated");
      }
    } catch (error) {
      toast.error("Failed to update alert");
    }
  };

  const handleTestAlert = async (alertId) => {
    setTestingId(alertId);
    setTestResults(null);
    try {
      const response = await fetch(`${API}/job-alerts/${alertId}/test`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestResults({
          alertId,
          count: data.matches_count,
          jobs: data.matching_jobs
        });
        toast.success(`Found ${data.matches_count} matching jobs`);
      }
    } catch (error) {
      toast.error("Failed to test alert");
    } finally {
      setTestingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      industries: [],
      employment_types: [],
      locations: [],
      keywords: [],
      min_salary: "",
      seniority_levels: [],
      frequency: "daily"
    });
    setKeywordInput("");
    setLocationInput("");
  };

  const startEditing = (alert) => {
    setEditingAlert(alert);
    setFormData({
      name: alert.name,
      industries: alert.industries || [],
      employment_types: alert.employment_types || [],
      locations: alert.locations || [],
      keywords: alert.keywords || [],
      min_salary: alert.min_salary?.toString() || "",
      seniority_levels: alert.seniority_levels || [],
      frequency: alert.frequency || "daily"
    });
    setShowCreateForm(false);
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData(prev => ({
        ...prev,
        keywords: [...prev.keywords, keywordInput.trim()]
      }));
      setKeywordInput("");
    }
  };

  const addLocation = () => {
    if (locationInput.trim() && !formData.locations.includes(locationInput.trim())) {
      setFormData(prev => ({
        ...prev,
        locations: [...prev.locations, locationInput.trim()]
      }));
      setLocationInput("");
    }
  };

  const alertFormJSX = (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-obsidian-light border border-[#333] p-6 mb-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading text-lg font-bold">
          {editingAlert ? "Edit Alert" : "Create New Alert"}
        </h3>
        <button
          onClick={() => {
            setShowCreateForm(false);
            setEditingAlert(null);
            resetForm();
          }}
          className="p-1 hover:bg-white/5 rounded"
        >
          <X className="w-5 h-5 text-gray-400" />
        </button>
      </div>
      
      <div className="space-y-5">
        {/* Alert Name */}
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Alert Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g., Remote Tech Jobs in California"
            className="bg-obsidian border-[#333] text-white"
          />
        </div>
        
        {/* Industries */}
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Industries</label>
          <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border border-[#222] rounded bg-obsidian">
            {industries.map(ind => (
              <button
                key={ind}
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    industries: prev.industries.includes(ind)
                      ? prev.industries.filter(i => i !== ind)
                      : [...prev.industries, ind]
                  }));
                }}
                className={`px-3 py-1 text-sm border transition-colors ${
                  formData.industries.includes(ind)
                    ? "border-crimson bg-crimson/20 text-crimson"
                    : "border-[#333] text-gray-400 hover:border-[#444]"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
          {formData.industries.length > 0 && (
            <div className="mt-2 text-xs text-gray-500">
              Selected: {formData.industries.length} {formData.industries.length === 1 ? 'industry' : 'industries'}
            </div>
          )}
        </div>
        
        {/* Employment Types */}
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Employment Types</label>
          <div className="flex flex-wrap gap-2 p-2 border border-[#222] rounded bg-obsidian">
            {employmentTypes.map(type => (
              <button
                key={type.value}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFormData(prev => ({
                    ...prev,
                    employment_types: prev.employment_types.includes(type.value)
                      ? prev.employment_types.filter(t => t !== type.value)
                      : [...prev.employment_types, type.value]
                  }));
                }}
                className={`px-3 py-1.5 text-sm border rounded-sm transition-all duration-150 select-none ${
                  formData.employment_types.includes(type.value)
                    ? "border-crimson bg-crimson/20 text-crimson font-medium"
                    : "border-[#333] text-gray-400 hover:border-[#444] hover:text-gray-300"
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Locations */}
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Locations</label>
          <div className="flex gap-2 mb-2">
            <Input
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              placeholder="Add location (e.g., Remote, New York)"
              className="bg-obsidian border-[#333] text-white flex-1"
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addLocation())}
            />
            <Button type="button" onClick={addLocation} variant="outline" className="border-[#333]">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {formData.locations.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.locations.map(loc => (
                <span key={loc} className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm flex items-center gap-2">
                  <MapPin className="w-3 h-3" />
                  {loc}
                  <button onClick={() => setFormData(prev => ({
                    ...prev,
                    locations: prev.locations.filter(l => l !== loc)
                  }))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Keywords */}
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Keywords</label>
          <div className="flex gap-2 mb-2">
            <Input
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              placeholder="Add keyword (e.g., Python, Manager)"
              className="bg-obsidian border-[#333] text-white flex-1"
              onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addKeyword())}
            />
            <Button type="button" onClick={addKeyword} variant="outline" className="border-[#333]">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          {formData.keywords.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.keywords.map(kw => (
                <span key={kw} className="px-3 py-1 bg-purple-500/20 text-purple-400 text-sm flex items-center gap-2">
                  <Search className="w-3 h-3" />
                  {kw}
                  <button onClick={() => setFormData(prev => ({
                    ...prev,
                    keywords: prev.keywords.filter(k => k !== kw)
                  }))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Min Salary & Frequency */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Minimum Salary</label>
            <Input
              type="number"
              value={formData.min_salary}
              onChange={(e) => setFormData(prev => ({ ...prev, min_salary: e.target.value }))}
              placeholder="e.g., 50000"
              className="bg-obsidian border-[#333] text-white"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Notification Frequency</label>
            <select
              value={formData.frequency}
              onChange={(e) => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
              className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white"
            >
              {FREQUENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[#333]">
          <Button
            variant="outline"
            className="border-[#333] text-white"
            onClick={() => {
              setShowCreateForm(false);
              setEditingAlert(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={editingAlert ? handleUpdateAlert : handleCreateAlert}
            disabled={saving}
            className="bg-crimson hover:bg-crimson-hover"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {editingAlert ? "Update Alert" : "Create Alert"}
          </Button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-obsidian" data-testid="job-alerts-page">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-crimson/10 border border-crimson/30 mb-3">
                <Bell className="w-3 h-3 text-crimson" />
                <span className="text-xs uppercase tracking-widest text-crimson font-medium">Smart Alerts</span>
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                JOB <span className="text-crimson">ALERTS</span>
              </h1>
              <p className="text-gray-500 text-sm mt-1">Get notified when jobs matching your criteria are posted</p>
            </div>
            
            {!showCreateForm && !editingAlert && (
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-crimson hover:bg-crimson-hover"
                data-testid="create-alert-btn"
              >
                <BellPlus className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            )}
          </div>

          {/* Create/Edit Form */}
          <AnimatePresence>
            {(showCreateForm || editingAlert) && alertFormJSX}
          </AnimatePresence>

          {/* Alerts List */}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-obsidian-light border border-[#222]">
              <BellOff className="w-16 h-16 text-gray-600 mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Job Alerts Yet</h3>
              <p className="text-gray-500 text-sm max-w-sm mb-6">
                Create custom alerts to get notified when jobs matching your preferences are posted.
              </p>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-crimson hover:bg-crimson-hover"
              >
                <BellPlus className="w-4 h-4 mr-2" />
                Create Your First Alert
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert, index) => (
                <motion.div
                  key={alert.alert_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-obsidian-light border p-5 transition-colors ${
                    alert.is_active ? "border-[#222] hover:border-[#333]" : "border-[#222] opacity-60"
                  }`}
                  data-testid={`alert-${alert.alert_id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Bell className={`w-5 h-5 ${alert.is_active ? "text-crimson" : "text-gray-500"}`} />
                        <h3 className="font-heading text-lg font-bold text-white">{alert.name}</h3>
                        {!alert.is_active && (
                          <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs">Paused</span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {alert.industries?.slice(0, 3).map(ind => (
                          <span key={ind} className="px-2 py-1 bg-obsidian border border-[#333] text-gray-400 text-xs flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {ind}
                          </span>
                        ))}
                        {alert.employment_types?.slice(0, 2).map(type => (
                          <span key={type} className="px-2 py-1 bg-obsidian border border-[#333] text-gray-400 text-xs flex items-center gap-1">
                            <Briefcase className="w-3 h-3" />
                            {type.replace("-", " ")}
                          </span>
                        ))}
                        {alert.locations?.slice(0, 2).map(loc => (
                          <span key={loc} className="px-2 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {loc}
                          </span>
                        ))}
                        {alert.keywords?.slice(0, 2).map(kw => (
                          <span key={kw} className="px-2 py-1 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs flex items-center gap-1">
                            <Search className="w-3 h-3" />
                            {kw}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {FREQUENCY_OPTIONS.find(f => f.value === alert.frequency)?.label || "Daily"}
                        </span>
                        {alert.matches_count > 0 && (
                          <span className="text-green-400">{alert.matches_count} matches</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestAlert(alert.alert_id)}
                        disabled={testingId === alert.alert_id}
                        className="border-[#333] text-white hover:border-crimson"
                        data-testid={`test-alert-${alert.alert_id}`}
                      >
                        {testingId === alert.alert_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleAlert(alert)}
                        className="border-[#333] text-white hover:border-crimson"
                      >
                        {alert.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing(alert)}
                        className="border-[#333] text-white hover:border-crimson"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteAlert(alert.alert_id)}
                        disabled={deletingId === alert.alert_id}
                        className="border-[#333] text-red-500 hover:border-red-500"
                      >
                        {deletingId === alert.alert_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Test Results */}
                  {testResults?.alertId === alert.alert_id && testResults.jobs?.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="mt-4 pt-4 border-t border-[#333]"
                    >
                      <p className="text-sm text-gray-400 mb-3">
                        <Check className="w-4 h-4 inline text-green-400 mr-1" />
                        Found {testResults.count} matching jobs
                      </p>
                      <div className="space-y-2">
                        {testResults.jobs.slice(0, 3).map(job => (
                          <Link
                            key={job.job_id}
                            to={`/jobs/${job.job_id}`}
                            className="block p-3 bg-obsidian border border-[#333] hover:border-crimson/50 transition-colors"
                          >
                            <p className="font-medium text-white text-sm">{job.title}</p>
                            <p className="text-gray-500 text-xs">{job.company_name} • {job.location}</p>
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default JobAlerts;
